import React from "react";

function TermsandConditionPage() {
  return (
    <div className="container mt-8">
      <h1 className="text-2xl font-bold">
        Magnum Car Rentals – Terms & Conditions of Booking
      </h1>
      <p className="text-xl my-4">
        <strong>Effective Date:</strong> Jan, 01, 2025
        <br />
        <strong>Location:</strong> Illinois, USA
      </p>

      <p>
        These Terms & Conditions (“Agreement”) govern all bookings and rentals
        made with <strong>Magnum Car Rentals</strong> (“Company,” “we,” or
        “us”). By booking and using our vehicles, you (“Guest,” “Renter,” or
        “you”) agree to comply with the terms outlined below.
      </p>
      <br />
      <h2 className="text-xl font-semibold mb-2">1. Eligibility to Rent</h2>

      <ul className="list-disc pl-6">
        <li>
          Renters must be <strong>at least 18 years old</strong> and hold a{" "}
          <strong>valid government-issued driver’s</strong> license recognized
          under Illinois law.
        </li>
        <li>
          International guests must present a valid international driving permit
          along with their home country driver’s license.
        </li>
        <li>
          Magnum Car Rentals reserves the right to decline service if proper
          identification or documentation is not provided.
        </li>
      </ul>
      <br />

      <h2 className="text-xl font-semibold mb-2">
        2. Driver’s License & Insurance Requirements
      </h2>

      <ul className="list-disc pl-6">
        <li>
          All guests must provide a <strong>valid driver’s license</strong>{" "}
          before taking possession of the vehicle.
        </li>
        <li>Guests must either:</li>
        <ul className="list-decimal pl-6">
          <li>
            Present valid proof of <strong>personal auto insurance</strong>{" "}
            covering rental cars, or
          </li>
          <li>Purchase coverage through an approved third-party provider.</li>
        </ul>
        <li>
          <strong>
            Any guest who does not provide insurance, or fails to verify their
            ID, shall be fully liable
          </strong>{" "}
          for all damages, collisions, or accidents during their trip.
        </li>
      </ul>
      <br />
      <h2 className="text-xl font-semibold mb-2">3. Deposits & Payments</h2>

      <ul className="list-disc pl-6">
        <li>
          A{" "}
          <strong>
            refundable deposit equal to 15% of the total trip cost
          </strong>{" "}
          is required at the time of booking.
        </li>
        <li>
          The deposit may be applied against unpaid balances, damages, tickets,
          tolls, or fines incurred during the trip.
        </li>
        <li>
          Full payment of the rental cost must be made prior to or at the time
          of vehicle collection.
        </li>
      </ul>
      <br />

      <h2 className="text-xl font-semibold mb-2">
        4. Additional Refundable Deposit Policy
      </h2>

      <p>
        To help manage risk, Magnum Car Rentals requires an additional
        refundable security deposit in the cases below. This deposit is in
        addition to the standard 15% Trip pre-authorization and is fully
        refundable after final inspection and accounting (bank processing times
        may vary).
      </p>
      <ul className="list-disc pl-6">
        <li>
          Out-of-state driver’s license (non-Illinois): +$200 refundable
          deposit,
        </li>
        <li>
          Driver’s license with violations and/or unpaid traffic violation and
          or tickets: +$250 refundable deposit,
        </li>
      </ul>
      <p>
        These deposits are paid at pickup, and released if no amounts are owed
        for damage, tolls, tickets, cleaning, late return, or other charges
        under our Terms.
      </p>
      <br />
      <h2 className="text-xl font-semibold mb-2">5. Use of Vehicle</h2>

      <ul className="list-disc pl-6">
        <li>
          The vehicle must only be operated by the renter and any additional
          drivers listed on the rental agreement.
        </li>
        <li>
          Vehicles must not be used for illegal activities, racing, off-road
          driving, or sub-rental.
        </li>
        <li>
          Guests are responsible for returning the vehicle with the{" "}
          <strong>same fuel level as provided at pick-up.</strong>
        </li>
      </ul>
      <br />

      <h2 className="text-xl font-semibold mb-2">
        6. Liability for Damages & Accidents
      </h2>

      <ul className="list-disc pl-6">
        <li>
          Guests are fully responsible for any{" "}
          <strong>accident, collision, or damage</strong> to the vehicle if
          insurance is not provided or verified.
        </li>
        <li>
          In the event of an accident, the guest must immediately notify both
          <strong>law enforcement</strong> and{" "}
          <strong>Magnum Car Rentals</strong>.
        </li>
        <li>
          Failure to report may result in forfeiture of the deposit and
          additional liability.
        </li>
      </ul>
      <br />

      <h2 className="text-xl font-semibold mb-2">
        7. Driver’s License Verification & Refund Policy
      </h2>

      <ul className="list-decimal pl-6">
        <li>
          <strong>Post-Trip Review</strong>
          <ul className="list-disc pl-6">
            <li>
              After each rental ends, Magnum Car Rentals will wait{" "}
              <strong>five (5) business days</strong> before refunding the
              deposit.
            </li>
            <li>
              During this period, we will review the guest’s driver’s license
              and driving record.
            </li>
          </ul>
        </li>
        <li>
          <strong>Purpose of Verification</strong>
          <ul className="list-disc pl-6">
            <li>
              This review checks for any{" "}
              <strong>tickets, tolls, or fines</strong> incurred during the
              rental.
            </li>
          </ul>
        </li>
        <li>
          <strong>Refund & Charges</strong>
          <ul className="list-disc pl-6">
            <li>
              If no tickets, tolls, or fines are found, the full refundable
              deposit will be returned.
            </li>
            <li>
              If tickets, tolls, or fines are identified, the corresponding
              amount will be{" "}
              <strong>deducted from the refundable deposit.</strong>
            </li>
            <li>
              If the deposit does not cover the full cost, the outstanding
              balance will be{" "}
              <strong>charged to the guest’s card on file.</strong> If charging
              the card is unsuccessful, an{" "}
              <strong>invoice will be issued</strong>, and the guest is required
              to pay promptly.
            </li>
            <li>
              Refund is will not be initiated if a trip is extended or rebooked
              or guest is still on trip with the same vehicle.
            </li>
          </ul>
        </li>
        <li>
          <strong>Guest Responsibility</strong>
          <ul className="list-disc pl-6">
            <li>
              Guests are fully responsible for all{" "}
              <strong>tickets, tolls, and fines</strong>
              during their rental.
            </li>
            <li>
              Failure to pay may result in additional fees, collection action,
              or legal remedies under Illinois law.
            </li>
          </ul>
        </li>
      </ul>
      <br />
      <h2 className="text-xl font-semibold mb-2">8. Cancellations & Refunds</h2>

      <ul className="list-disc pl-6">
        <li>
          Guests may cancel their booking up to{" "}
          <strong>24 hours before the scheduled rental start time</strong> for a
          full refund.
        </li>
        <li>
          Cancellations made less than <strong>24 hours in advance</strong> will
          result in forfeiture of the deposit.
        </li>
        <li>
          No-shows are considered cancellations and may be charged the full
          rental amount.
        </li>
      </ul>
      <br />
      <h2 className="text-xl font-semibold mb-2">9. Indemnity</h2>

      <ul className="list-disc pl-6">
        <li>
          Guests agree to indemnify and hold <strong>Magnum Car Rentals</strong>{" "}
          harmless against all claims, losses, liabilities, costs, and damages
          arising from their use of the vehicle.
        </li>
      </ul>
      <br />
      <h2 className="text-xl font-semibold mb-2">10. Governing Law</h2>

      <ul className="list-disc pl-6">
        <li>
          These Terms & Conditions are governed by and shall be interpreted
          under the <strong>laws of the State of Illinois, USA.</strong>
        </li>
        <li>
          Any disputes shall be resolved under the exclusive jurisdiction of
          Illinois state courts.
        </li>
      </ul>
      <br />
      <h2 className="text-xl font-semibold mb-2">11. Consent to Receive SMS Notifications</h2>

      <ul className="list-disc pl-6">
        <li>
          With my signature, I agree to receive SMS messages from Magnum Car
          Rentals related to my rental inquiry, updates, appointments, and
          promotional offers. Message and data rates may apply. I may reply STOP
          at any time to unsubscribe.
        </li>
      </ul>
      <br />
    </div>
  );
}

export default TermsandConditionPage;
